import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  FlatList,
  Modal,
  Platform,
  ScrollView,
  Switch,
  Text,
  TextInput as RNTextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import ConfirmDiscardModal from '../../../../components/ConfirmDiscardModal';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import LHOverlay from '../../../../components/LHOverlay';
import LhTextInput from '../../../../components/LhTextInput';
import LHDropDown from '../../../../components/LHDropDown';
import LhButton from '../../../../components/LhButton';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme';
import { AppDispatch, RootState } from '../../../../store';
import {
  createSiteEvaluationQuestion,
  updateSiteEvaluationQuestion,
  fetchSiteEvaluationHeaders,
  fetchSiteEvaluationSubHeaders,
  patchSiteEvaluationStatus,
} from '../../../../store/slices/adminSlice';
import {
  LOCATION_TYPE_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  SPARC_CATEGORY_OPTIONS,
  THREE_PL_OPTIONS,
} from './kfaDropdownOptions';
import { getValidationState, REQUIRED_FIELDS_MESSAGE } from './validationUtils';
import { styles, msStyles } from './SiteEvaluationOverlay.styles';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Option {
  id: string | number;
  displayText: string;
}

/** Full dataset for edit mode — mirrors expected API response fields */
export interface SiteEvaluationEditItem {
  id: string;
  /** The sectionType — "HEADER" or "DATA" (case may vary from API) */
  sectionType?: string;
  /** Primary text: Title for HEADER, Data value for DATA */
  sectionValue?: string;
  /** Section header text — used to resolve Header dropdown on edit for DATA type */
  sectionHeader?: string | null;
  /** Section sub-header text — used to resolve Sub-Header dropdown on edit */
  sectionSubHeader?: string | null;
  locationType?: string | null;
  status?: boolean | null;
  organizationType?: string | null;
  threePlStatus?: boolean | string | null;
  category?: string | null;
  /** ISO timestamp from the API — used to enforce the 24-hour edit lock on text fields */
  createdAt?: string;
}

interface SiteEvaluationOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  mode?: 'create' | 'edit';
  selectedItem?: SiteEvaluationEditItem;
  onSaveSuccess?: () => void;
}

interface SiteEvaluationValidationErrors {
  sectionValue: boolean;
  header: boolean;
  locationType: boolean;
  organizationType: boolean;
  threePlStatus: boolean;
  category: boolean;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const findOption = <T extends Option>(
  options: T[],
  value: string | number | undefined | null,
): T | null => {
  if (value === undefined || value === null || value === '') return null;
  return options.find((o) => String(o.id).toLowerCase() === String(value).toLowerCase()) ?? null;
};

/** Find all matching options from a comma-separated string. */
const findOptions = <T extends Option>(
  options: T[],
  value: string | undefined | null,
): T[] => {
  if (!value) return [];
  const parts = String(value).split(',').map((s) => s.trim()).filter(Boolean);
  return options.filter((o) =>
    parts.some((v) => String(o.id).toLowerCase() === v.toLowerCase()),
  );
};

// ─── MSOption + MultiSelectDropdown ───────────────────────────────────────────

interface MSOption {
  id: string | number;
  displayText: string;
}

interface MultiSelectDropdownProps {
  label?: string;
  options: MSOption[];
  selected: MSOption[];
  onChange: (selected: MSOption[]) => void;
  placeholder?: string;
  singleSelect?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label, options, selected, onChange, placeholder = 'Select...', singleSelect = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [menuStyle, setMenuStyle] = React.useState({ top: 0, left: 0, width: 200 });
  const triggerRef = React.useRef<any>(null);

  const toggle = () => {
    if (!open && triggerRef.current) {
      if (Platform.OS === 'web') {
        try {
          const rect = (triggerRef.current as any).getBoundingClientRect();
          const scrollY = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
          setMenuStyle({ top: rect.bottom + scrollY, left: rect.left, width: rect.width });
        } catch (_) { /* ignore */ }
      } else {
        triggerRef.current.measureInWindow?.((x: number, y: number, w: number, h: number) => {
          setMenuStyle({ top: y + h, left: x, width: w });
        });
      }
    }
    setOpen((o) => !o);
  };

  const isSelected = (item: MSOption) => selected.some((s) => String(s.id) === String(item.id));
  const handlePress = (item: MSOption) => {
    if (singleSelect) {
      onChange([item]);
      setOpen(false);
      setSearch('');
      return;
    }
    if (isSelected(item)) onChange(selected.filter((s) => String(s.id) !== String(item.id)));
    else onChange([...selected, item]);
  };
  const filtered = options.filter((o) => o.displayText.toLowerCase().includes(search.toLowerCase()));
  const displayText = selected.length === 0 ? placeholder : selected.map((s) => s.displayText).join(', ');

  return (
    <View style={msStyles.container}>
      {label ? <Text style={msStyles.label}>{label}</Text> : null}
      <TouchableOpacity ref={triggerRef} style={msStyles.trigger} onPress={toggle} activeOpacity={0.8}>
        <Text style={[msStyles.triggerText, selected.length === 0 && msStyles.placeholder]} numberOfLines={1}>{displayText}</Text>
        <MaterialIcons name={open ? 'arrow-drop-up' : 'arrow-drop-down'} size={20} color="#495057" />
      </TouchableOpacity>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={msStyles.backdrop} activeOpacity={1} onPress={() => setOpen(false)} />
        <View style={[msStyles.menu, Platform.OS === 'web'
          ? ({ position: 'fixed', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width } as any)
          : { position: 'absolute', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width }]}>
          <View style={msStyles.searchBox}>
            <MaterialIcons name="search" size={16} color="#888" style={{ marginRight: 6 }} />
            <RNTextInput value={search} onChangeText={setSearch} placeholder="Search..." placeholderTextColor="#AAAAAA" style={msStyles.searchInput} />
          </View>
          <FlatList data={filtered} keyExtractor={(item) => String(item.id)} style={{ maxHeight: 200 }} keyboardShouldPersistTaps="always"
            renderItem={({ item }) => (
              <TouchableOpacity style={[msStyles.option, isSelected(item) && msStyles.optionSelected]} onPress={() => handlePress(item)}>
                <Text style={msStyles.optionText}>{item.displayText}</Text>
                {isSelected(item) && <MaterialIcons name="check" size={18} color="#0066CC" />}
              </TouchableOpacity>
            )} />
        </View>
      </Modal>
    </View>
  );
};


// ─── mapRowToForm ───────────────────────────────────────────────────────────────

const mapRowToForm = (item?: SiteEvaluationEditItem) => {
  const normalizedThreePl =
    item?.threePlStatus === true || String(item?.threePlStatus ?? '').toUpperCase() === 'YES'
      ? 'Yes'
      : item?.threePlStatus === false || String(item?.threePlStatus ?? '').toUpperCase() === 'NO'
      ? 'No'
      : null;

  return {
    textValue: String(item?.sectionValue ?? ''),
    sectionHeader: item?.sectionHeader ?? null,
    sectionSubHeader: item?.sectionSubHeader ?? null,
    locationType: findOptions(LOCATION_TYPE_OPTIONS, item?.locationType),
    organizationType: findOptions(ORGANIZATION_TYPE_OPTIONS, item?.organizationType),
    threePlStatus: findOption(THREE_PL_OPTIONS, normalizedThreePl),
    category: findOptions(SPARC_CATEGORY_OPTIONS, item?.category),
    statusEnabled: item?.status !== false,
  };
};

// ─── Component ─────────────────────────────────────────────────────────────────

const SiteEvaluationOverlay: React.FC<SiteEvaluationOverlayProps> = ({
  visible,
  onDismiss,
  mode = 'create',
  selectedItem,
  onSaveSuccess,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const dispatch = useDispatch<AppDispatch>();

  // ─── Redux: Site Evaluation Headers & Sub-Headers ────────────────────────────
  const siteEvalHeaders = useSelector(
    (s: RootState) => s.configuration.siteEvaluation.headers,
  );
  const headersLoaded = useSelector(
    (s: RootState) => s.configuration.siteEvaluation.headersLoaded,
  );
  const siteEvalSubHeaders = useSelector(
    (s: RootState) => s.configuration.siteEvaluation.subHeaders,
  );
  const subHeadersLoaded = useSelector(
    (s: RootState) => s.configuration.siteEvaluation.subHeadersLoaded,
  );

  // Fetch headers and sub-headers every time the overlay opens (add or edit)
  useEffect(() => {
    if (visible) {
      dispatch(fetchSiteEvaluationHeaders());
      dispatch(fetchSiteEvaluationSubHeaders());
    }
  }, [visible, dispatch]);

  // Build deduplicated Header dropdown options from Redux
  const headerOptions = useMemo<Option[]>(() => {
    const seen = new Set<string>();
    return siteEvalHeaders
      .filter((h) => {
        const key = String(h.sectionValue ?? '')
          .replace(/:/g, '')
          .trim()
          .toLowerCase() || `id:${String(h.id)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((h) => ({
        id: String(h.id),
        displayText: h.sectionValue ?? `Header ${h.id}`,
      }));
  }, [siteEvalHeaders]);

  // ─── Form state ────────────────────────────────────────────────────────────────

  const [selectedHeader, setSelectedHeader] = useState<any>(null);
  const [selectedSubHeader, setSelectedSubHeader] = useState<any>(null);
  const [textValue, setTextValue] = useState('');
  const [locationType, setLocationType] = useState<MSOption[]>([]);
  const [organizationType, setOrganizationType] = useState<MSOption[]>([]);
  const [threePlStatus, setThreePlStatus] = useState<any>(null);
  const [category, setCategory] = useState<MSOption[]>([]);
  const [statusEnabled, setStatusEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // 24-hour text-field lock
  const isTextLocked = mode === 'edit' && !!selectedItem?.createdAt
    && (Date.now() - new Date(selectedItem.createdAt).getTime()) > 24 * 60 * 60 * 1000;

  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<SiteEvaluationValidationErrors>({
    sectionValue: false,
    header: false,
    locationType: false,
    organizationType: false,
    threePlStatus: false,
    category: false,
  });

  // Sub-header options filtered by the currently selected header
  const subHeaderOptions = useMemo<Option[]>(() => {
    if (!selectedHeader) return [];
    const selectedHeaderId = Number(selectedHeader.id);
    return siteEvalSubHeaders
      .filter((sh) => sh.parentId === selectedHeaderId)
      .map((sh) => ({
        id: String(sh.id),
        displayText: sh.sectionValue ?? `Sub-Header ${sh.id}`,
      }));
  }, [siteEvalSubHeaders, selectedHeader]);

  const isDirtyRef = useRef(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const resetForm = () => {
    setSelectedHeader(null);
    setSelectedSubHeader(null);
    setTextValue('');
    setLocationType([]);
    setOrganizationType([]);
    setThreePlStatus(null);
    setCategory([]);
    setStatusEnabled(true);
    setIsSaving(false);
    setValidationMessage(null);
    setFieldErrors({
      sectionValue: false,
      header: false,
      locationType: false,
      organizationType: false,
      threePlStatus: false,
      category: false,
    });
    isDirtyRef.current = false;
  };

  const clearFieldError = (field: keyof SiteEvaluationValidationErrors) => {
    isDirtyRef.current = true;
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validateForm = (formData: {
    sectionValue: string;
    selectedHeader: any;
    locationType: any;
    organizationType: any;
    threePlStatus: any;
    category: any;
  }) => {
    const { fieldErrors: nextErrors, hasErrors } = getValidationState<keyof SiteEvaluationValidationErrors>({
      sectionValue: !formData.sectionValue.trim(),
      header: !formData.selectedHeader,
      locationType: locationType.length === 0,
      organizationType: organizationType.length === 0,
      threePlStatus: !formData.threePlStatus,
      category: category.length === 0,
    });
    setFieldErrors(nextErrors);

    if (hasErrors) {
      setValidationMessage(REQUIRED_FIELDS_MESSAGE);
      return false;
    }
    return true;
  };

  // ─── Populate form on open ────────────────────────────────────────────────────

  useEffect(() => {
    if (!visible) {
      resetForm();
      return;
    }

    if (mode === 'edit' && selectedItem) {
      const mapped = mapRowToForm(selectedItem);
      setTextValue(mapped.textValue);
      setLocationType(mapped.locationType);
      setOrganizationType(mapped.organizationType);
      setThreePlStatus(mapped.threePlStatus);
      setCategory(mapped.category);
      setStatusEnabled(mapped.statusEnabled);
      return;
    }

    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, mode, selectedItem]);

  // Once headers load, resolve the Header dropdown for edit mode
  useEffect(() => {
    if (!visible || mode !== 'edit' || !selectedItem || headerOptions.length === 0) return;
    const targetHeader = (selectedItem.sectionHeader ?? '').trim().toLowerCase();
    if (!targetHeader) return;
    const match = headerOptions.find(
      (opt) => opt.displayText.trim().toLowerCase() === targetHeader,
    );
    if (match) setSelectedHeader(match);
  }, [headerOptions, visible, mode, selectedItem]);

  // Once sub-headers load and header is resolved, resolve the Sub-Header dropdown for edit mode
  useEffect(() => {
    if (!visible || mode !== 'edit' || !selectedItem || !selectedHeader || siteEvalSubHeaders.length === 0) return;
    const targetSubHeader = (selectedItem.sectionSubHeader ?? '').trim().toLowerCase();
    if (!targetSubHeader) return;
    const selectedHeaderId = Number(selectedHeader.id);
    const match = siteEvalSubHeaders.find(
      (sh) => sh.parentId === selectedHeaderId &&
        (sh.sectionValue ?? '').trim().toLowerCase() === targetSubHeader,
    );
    if (match) setSelectedSubHeader({ id: String(match.id), displayText: match.sectionValue ?? `Sub-Header ${match.id}` });
  }, [siteEvalSubHeaders, selectedHeader, visible, mode, selectedItem]);

  // ─── Scroll lock (web) ────────────────────────────────────────────────────────

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (visible) {
      const scrollY = (window as any).scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      (document.body.dataset as any).siteEvalOverlayScrollY = String(scrollY);
    } else {
      const savedY = parseInt((document.body.dataset as any).siteEvalOverlayScrollY || '0', 10);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedY);
    }
    return () => {
      const savedY = parseInt((document.body.dataset as any).siteEvalOverlayScrollY || '0', 10);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedY);
    };
  }, [visible]);

  // ─── Payload builders ─────────────────────────────────────────────────────────

  const buildSharedFields = () => ({
    sectionValue: textValue.trim(),
    locationType: locationType.length > 0 ? locationType.map((x) => String(x.id)).join(',') : null,
    status: statusEnabled,
    organizationType: organizationType.length > 0 ? organizationType.map((x) => String(x.id)).join(',') : null,
    threePlStatus:
      threePlStatus?.id === 'Yes' ? true : threePlStatus?.id === 'No' ? false : null,
    category: category.length > 0 ? category.map((x) => String(x.id)).join(',') : null,
  });

  const buildPostPayload = () => {
    const fields = buildSharedFields();
    // Send sub-header id if selected, otherwise fall back to header id
    const parentId = selectedSubHeader?.id
      ? Number(selectedSubHeader.id)
      : selectedHeader?.id ? Number(selectedHeader.id) : null;
    return {
      existingHeaderId: parentId,
      items: [{ sectionType: 'DATA' as const, ...fields }],
    };
  };

  const buildPutPayload = () => {
    const fields = buildSharedFields();
    const parentId = selectedSubHeader?.id
      ? Number(selectedSubHeader.id)
      : selectedHeader?.id ? Number(selectedHeader.id) : null;
    return {
      sectionType: 'DATA' as const,
      existingHeaderId: parentId,
      ...fields,
    };
  };

  // ─── Save handler ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const isValid = validateForm({
      sectionValue: textValue,
      selectedHeader,
      locationType,
      organizationType,
      threePlStatus,
      category,
    });

    if (!isValid) {
      return;
    }

    setIsSaving(true);
    setValidationMessage(null);
    try {
      if (mode === 'edit' && selectedItem?.id) {
        const payload = buildPutPayload();
        console.log('[SITE EVAL PUT] id/payload →', selectedItem.id, JSON.stringify(payload));
        await dispatch(updateSiteEvaluationQuestion({ id: selectedItem.id, ...payload } as any)).unwrap();
        await dispatch(patchSiteEvaluationStatus({ id: selectedItem.id, status: statusEnabled })).unwrap();
      } else {
        const payload = buildPostPayload();
        console.log('[SITE EVAL POST] payload →', JSON.stringify(payload));
        await dispatch(createSiteEvaluationQuestion(payload as any)).unwrap();
      }
      onDismiss();
      onSaveSuccess?.();
    } catch (error: any) {
      setValidationMessage(error?.message ?? 'Failed to save Site Evaluation question.');
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────────
  const handleCancel = () => {
    if (isDirtyRef.current) {
      setShowDiscardConfirm(true);
    } else {
      onDismiss();
    }
  };
  return (
    <>
      <ConfirmDiscardModal
        visible={showDiscardConfirm}
        onKeepEditing={() => setShowDiscardConfirm(false)}
        onDiscard={() => { setShowDiscardConfirm(false); onDismiss(); }}
      />
      <LHOverlay
        visible={visible}
        onDismiss={handleCancel}
        title={mode === 'edit' ? 'Edit Site Evaluation' : 'Add Site Evaluation'}
        size="medium"
        position="center"
        close={true}
      >
      <View style={[styles.overlayWrapper, isDark && { backgroundColor: colors.background.surface }]}>
        {validationMessage ? (
          <View style={styles.saveErrorBox}>
            <MaterialIcons
              name="error-outline"
              size={16}
              color="#C62828"
              style={{ marginRight: 8 }}
            />
            <Text style={styles.saveErrorText}>{validationMessage}</Text>
          </View>
        ) : null}

        <ScrollView style={styles.scrollContent} contentContainerStyle={styles.overlayContent}>

          {mode === 'edit' && (
            <View style={[styles.statusRow, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              <Text style={[styles.statusLabel, isDark && { color: colors.text.primary }]}>Status:</Text>
              <Switch
                value={statusEnabled}
                onValueChange={(val) => {
                  isDirtyRef.current = true;
                  setStatusEnabled(val);
                }}
                trackColor={{ false: '#CCCCCC', true: '#2E7D32' }}
                thumbColor="#FFFFFF"
              />
              <Text
                style={[
                  styles.statusValue,
                  { color: statusEnabled ? (isDark ? '#4CAF50' : '#2E7D32') : (isDark ? colors.text.disabled : '#757575') },
                ]}
              >
                {statusEnabled ? 'Enabled' : 'Disabled'}
              </Text>
            </View>
          )}

          {/* ── 1. Module Assignment ─────────────────────────────────────────── */}
          <View style={[styles.moduleAssignmentSection, isDark && { backgroundColor: colors.background.elevated, borderLeftColor: '#F57F17' }]}>
            <Text style={[styles.moduleSectionTitle, isDark && { color: colors.text.primary }]}>Module Assignment</Text>

            <View style={[styles.fullWidthDropdown, fieldErrors.header && styles.dropdownErrorWrapper]}>
              <View style={styles.labelRow}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Header</Text>
                <Text style={styles.requiredLabel}>*Required</Text>
              </View>
              <LHDropDown
                label=""
                childList={headerOptions}
                selectedValue={selectedHeader}
                setSelectedValue={(val: any) => {
                  setSelectedSubHeader(null);
                  setSelectedHeader(val);
                  clearFieldError('header');
                  setValidationMessage(null);
                }}
                placeholder={headersLoaded ? 'Select Header' : 'Loading headers...'}
              />
            </View>

            <View style={[styles.fullWidthDropdown, { marginTop: 12 }]}>
              <View style={styles.labelRow}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Sub-Header</Text>
                <Text style={[styles.requiredLabel, { color: isDark ? colors.text.disabled : '#757575' }]}>Optional</Text>
              </View>
              <LHDropDown
                key={selectedHeader?.id ?? 'no-header'}
                label=""
                childList={subHeaderOptions}
                selectedValue={selectedSubHeader}
                setSelectedValue={(val: any) => {
                  setSelectedSubHeader(val);
                  setValidationMessage(null);
                }}
                placeholder={
                  !selectedHeader
                    ? 'Select a Header first'
                    : subHeaderOptions.length === 0
                    ? 'No sub-headers for this header'
                    : 'Select Sub-Header'
                }
              />
            </View>
          </View>

          {/* ── 2. Assessment Text ───────────────────────────────────────────── */}
          <View style={styles.assessmentSection}>
            <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.default }]}>
              <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Assessment Text</Text>
            </View>

            <View style={styles.inputGroup}>
              <View style={styles.labelRow}>
                <Text style={[styles.inputLabel, isDark && { color: colors.text.primary }]}>Data</Text>
                <Text style={styles.requiredLabel}>*Required</Text>
              </View>
              <LhTextInput
                value={textValue}
                onChangeText={(val) => {
                  setTextValue(val);
                  clearFieldError('sectionValue');
                  setValidationMessage(null);
                }}
                placeholder="e.g., Conduct safety check"
                multiline
                numberOfLines={3}
                editable={!isTextLocked}
                style={styles.textInput}
                wrapperStyle={[styles.inputWrapper, fieldErrors.sectionValue && styles.inputError, isTextLocked && styles.inputLocked]}
              />
              <View style={styles.charCountRow}>
                {isTextLocked && <Text style={styles.lockedHelperText}>Locked 24 hours after creation.</Text>}
                <Text style={styles.charCount}>{textValue.length}/500</Text>
              </View>
            </View>
          </View>

          {/* ── 3. Shared Attributes ─────────────────────────────────────────── */}
          <View style={styles.sharedAttributesSection}>
            <View style={[styles.sectionTitleContainer, isDark && { borderBottomColor: colors.border.default }]}>
              <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Shared Attributes</Text>
            </View>

            <View style={styles.dropdownRow}>
              <View style={styles.dropdownColumn}>
                <View style={fieldErrors.locationType && styles.dropdownErrorWrapper}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Location Type</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <MultiSelectDropdown
                    label=""
                    options={LOCATION_TYPE_OPTIONS}
                    selected={locationType}
                    onChange={(val) => { setLocationType(val); clearFieldError('locationType'); setValidationMessage(null); }}
                    placeholder="Select Location Type"
                  />
                </View>
              </View>
              <View style={styles.dropdownColumn}>
                <View style={fieldErrors.organizationType && styles.dropdownErrorWrapper}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Organization Type</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <MultiSelectDropdown
                    label=""
                    options={ORGANIZATION_TYPE_OPTIONS}
                    selected={organizationType}
                    onChange={(val) => { setOrganizationType(val); clearFieldError('organizationType'); setValidationMessage(null); }}
                    placeholder="Select Organization Type"
                  />
                </View>
              </View>
            </View>

            <View style={styles.dropdownRow}>
              <View style={styles.dropdownColumn}>
                <View style={fieldErrors.threePlStatus && styles.dropdownErrorWrapper}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>3PL Status</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <LHDropDown
                    label=""
                    childList={THREE_PL_OPTIONS}
                    selectedValue={threePlStatus}
                    setSelectedValue={(val: any) => {
                      setThreePlStatus(val);
                      clearFieldError('threePlStatus');
                      setValidationMessage(null);
                    }}
                    placeholder="Select 3PL Status"
                  />
                </View>
              </View>
              <View style={styles.dropdownColumn}>
                <View style={fieldErrors.category && styles.dropdownErrorWrapper}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.fieldLabel, isDark && { color: colors.text.primary }]}>Category</Text>
                    <Text style={styles.requiredLabel}>*Required</Text>
                  </View>
                  <MultiSelectDropdown
                    label=""
                    options={SPARC_CATEGORY_OPTIONS}
                    selected={category}
                    onChange={(val) => { setCategory(val); clearFieldError('category'); setValidationMessage(null); }}
                    placeholder="Select Category"
                    singleSelect
                  />
                </View>
              </View>
            </View>
          </View>

        </ScrollView>

        <View style={styles.overlayFooter}>
          <View style={styles.footerButtons}>
            <LhButton text="Cancel" onPress={handleCancel} variant="secondary" disabled={isSaving} />
            <LhButton text="Save" onPress={handleSave} variant="primary" isLoading={isSaving} disabled={isSaving} />
          </View>
        </View>
      </View>
    </LHOverlay>
    </>
  );
};

export default SiteEvaluationOverlay;

// ─── Styles ────────────────────────────────────────────────────────────────────

